import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { SeoMeta, SchemaOrg } from '@/components/base/SeoHead';
import { siteInfo } from '@/mocks/matches';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const article = useMemo(
    () => articles.find((a) => a.id === id),
    [id]
  );

  const relatedArticles = useMemo(
    () => articles.filter((a) => a.id !== id && a.category === article?.category).slice(0, 3),
    [id, article]
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">您访问的文章不存在或已被删除</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium text-sm no-underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabels: Record<string, string> = {
    football: '足球竞猜',
    basketball: '篮球押注',
    tennis: '网球竞猜',
  };
  const categoryName = categoryLabels[article.category] || article.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      logo: { '@type': 'ImageObject', url: `${siteInfo.url}/logo.png` },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `${siteInfo.url}/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `${siteInfo.url}/article/${article.id}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SeoMeta
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        keywords={`${siteInfo.keywords},${article.tags.join(',')}`}
        canonicalUrl={`${siteInfo.url}/article/${article.id}`}
        ogType="article"
        ogImage={article.coverImage}
      />
      <SchemaOrg schema={articleSchema} />
      <SchemaOrg schema={breadcrumbSchema} />

      <Navbar />

      <main className="pt-16 bg-background-50">
        <div className="max-w-[860px] mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryName, to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <article>
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500 whitespace-nowrap">#{tag}</span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/70">
                <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                <span><i className="ri-calendar-line mr-1"></i>{article.publishDate}</span>
                <span><i className="ri-time-line mr-1"></i>阅读 {article.readTime}</span>
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-loose prose-p:mb-4
                [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_p]:text-foreground-700 [&_p]:leading-loose [&_p]:mb-4 [&_p]:text-sm md:[&_p]:text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70 flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-14 mb-10">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((relArticle) => (
                  <ArticleCard key={relArticle.id} article={relArticle} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}